import { useCallback, useMemo, useRef, useState } from 'react';

import { isLoaded, isLoading } from 'src/app/shared/utils/async';

import type { GeoScope, Place } from '../shared/types';

import { usePlacePredictions } from './usePlacePredictions';

type PlaceFieldHookProps = {
	geoScope?: GeoScope;
	defaultPlaceValue: Place | null;
	onSelect?: (place: Place | null) => void;
};

type PlaceFieldState = {
	placeInputValue: string;
	onPlaceInputChange: (newInputValue: string) => void;
	onPlaceFocus: () => void;
	onPlaceBlur: () => void;
	placeValue: Place | null;
	onPlaceChange: (newValue: Place | null) => void;
	placePredictions: Place[];
};

export const usePlaceField = ({ geoScope, defaultPlaceValue, onSelect }: PlaceFieldHookProps): PlaceFieldState => {
	const [placeInputValue, setPlaceInputValue] = useState(defaultPlaceValue?.displayName ?? '');
	const [placeValue, setPlaceValue] = useState(defaultPlaceValue);
	const isFocusedRef = useRef(false);

	const placePredictionsResult = usePlacePredictions({ input: placeInputValue, geoScope });
	const placePredictions = useMemo(
		() => (isLoaded(placePredictionsResult) ? placePredictionsResult.data : []),
		[placePredictionsResult],
	);

	const onPlaceInputChange = (newValue: string) => {
		// ComboBox fires onInputChange('') after blur — ignore when a place is selected
		if (!isFocusedRef.current && newValue === '' && placeValue !== null) return;
		setPlaceInputValue(newValue);
		if (newValue === '') {
			setPlaceValue(null);
			onSelect?.(null);
		}
	};

	const onPlaceFocus = () => {
		isFocusedRef.current = true;
	};

	const onPlaceBlur = () => {
		isFocusedRef.current = false;
	};

	const onPlaceChange = useCallback(
		(selectedPlace: Place | null) => {
			if (isLoading(placePredictionsResult)) return;
			setPlaceValue(selectedPlace);
			onSelect?.(selectedPlace);
		},
		[onSelect, placePredictionsResult],
	);

	return {
		placeInputValue,
		onPlaceInputChange,
		onPlaceFocus,
		onPlaceBlur,
		placeValue,
		onPlaceChange,
		placePredictions,
	};
};
